import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '@environments';
import { Utils as UtilsLogonPortal } from '../utils/utils';
import { IamResponseModel } from '../interfaces/iam-response-model.interface';

@Injectable({
  providedIn: 'root',
})
export class RenovateService {
  private isRefreshing = false;
  
  public tokenSubject: Subject<string | null> = new Subject<string | null>();
  public renovateErrorSubject: Subject<HttpErrorResponse | null> = new Subject();

  constructor(
    private readonly _http: HttpClient,
    private readonly _jwtHelper: JwtHelperService,
  ) { }

  private getToken(): string {
    return UtilsLogonPortal.getCookie("accessToken");
  }

  public renovateToken(): Observable<IamResponseModel> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.getToken()}`,
    });

    const url = `${UtilsLogonPortal.getBaseUrl(environment.env)}/security/iam/v1/user-identities/renovate-token`;

    return this._http
    .post<IamResponseModel>(
        url,
        {
          deviceFingerprint: localStorage.getItem('deviceFingerprint'),
        },
        { headers },
      )
      .pipe(
        tap((response) => {
          const accessToken: any = response.accessToken;
          this.isRefreshing = false;

          UtilsLogonPortal.setCookie('accessToken', accessToken);

          this.tokenSubject.next(accessToken);
        }),
        catchError((error) => {
          this.renovateErrorSubject.next(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public ensureToken(): Observable<string | null> {
    const token = this.getToken();
    if (this.isRefreshing) {
      return this.tokenSubject.asObservable();
    }

    if (token && !this.isTokenExpired(token)) {
      return of(token);
    }

    this.isRefreshing = true;
    this.tokenSubject = new Subject<string | null>();

    return this.renovateToken().pipe(
      map((value) => value.accessToken),
      catchError((error ) => {
        setTimeout(() => {
          this.isRefreshing = false;
        }, 5000)
        return throwError(() => new Error(error));
      })
    ) as any;
  }

  private isTokenExpired(token: string): boolean {
    return this._jwtHelper.isTokenExpired(token);
  }
}
